/**
 * Service for product tag-related API calls
 */

/**
 * Get all product tags
 * @returns {Promise<Array>} Array of tag objects
 */
export const getTags = async (storeId = null) => {
  try {
    const url = storeId ? `/api/product_tags?store_id=${storeId}` : '/api/product_tags';
    const response = await fetch(url, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch product tags: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error fetching product tags:', error);
    throw error;
  }
};

/**
 * Get a specific product tag by ID
 * @param {number} id - Tag ID
 * @returns {Promise<Object>} Tag object
 */
export const getTagById = async (id) => {
  try {
    const response = await fetch(`/api/product_tags/${id}`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch product tag ${id}: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching product tag ${id}:`, error);
    throw error;
  }
};

/**
 * Create a new product tag
 * @param {Object} tagData - Tag data
 * @returns {Promise<Object>} Created tag object
 */
export const createTag = async (tagData) => {
  try {
    const response = await fetch('/api/product_tags', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(tagData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to create product tag: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error('Error creating product tag:', error);
    throw error;
  }
};

/**
 * Update an existing product tag
 * @param {number} id - Tag ID
 * @param {Object} tagData - Updated tag data
 * @returns {Promise<Object>} Updated tag object
 */
export const updateTag = async (id, tagData) => {
  try {
    const response = await fetch(`/api/product_tags/${id}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(tagData)
    });
    
    if (!response.ok) {
      throw new Error(`Failed to update product tag ${id}: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error updating product tag ${id}:`, error);
    throw error;
  }
};

/**
 * Delete a product tag
 * @param {number} id - Tag ID
 * @returns {Promise<Object>} Response object
 */
export const deleteTag = async (id) => {
  try {
    const response = await fetch(`/api/product_tags/${id}`, {
      method: 'DELETE',
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to delete product tag ${id}: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error deleting product tag ${id}:`, error);
    throw error;
  }
};

/**
 * Get products associated with a specific tag
 * @param {number} tagId - Tag ID
 * @returns {Promise<Array>} Array of product objects
 */
export const getProductsByTagId = async (tagId) => {
  try {
    const response = await fetch(`/api/product_tags/${tagId}/products`, {
      credentials: 'include'
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch products for tag ${tagId}: ${response.status}`);
    }
    
    return response.json();
  } catch (error) {
    console.error(`Error fetching products for tag ${tagId}:`, error);
    throw error;
  }
};
